import React, { useState } from "react";
import ModalBox from "./ModalBox";
import LogoModalHeader from "./LogoModalHeader";
import GoogleOAuthButton from "./GoogleOAuthButton";
import { useLocation, useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useAuth } from "./AuthProvider";
import { subscribeEmail } from "../service/auth";
import { isValidEmail } from "../helpers/globals";
import { handleAuthSuccessRedirect } from "../utils/authRedirect";
import "./WelcomeModal.css";

function WelcomeModal({ onLoginClick }) {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSuccessfulSignup } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate email
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    if (!isValidEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsLoading(true);

    try {
      // Sign up with email
      const response = await subscribeEmail({
        email: email.trim(),
        newsletter_on: true,
        zebra_id: "PartyParty-Free-Trial-Mar26",
      });

      // Store auth token and user data
      const token = response.data?.token || response.data?.data?.token;
      const userData = response.data?.data || response.data;

      if (token) {
        // Update auth state immediately
        handleSuccessfulSignup(token, userData);

        localStorage.setItem("hasCreatedAccount", "true");

        handleAuthSuccessRedirect(navigate, location);
      } else {
        throw new Error("Invalid response from signup API");
      }
    } catch (err) {
      console.error("Email signup error:", err);
      setError(
        err.response?.data?.message ||
        err.message ||
        "Failed to sign up. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalBox open onClose={() => { }} backdrop="static" keyboard={false}>
      <div className="welcome-modal-wrapper">
        {/* Login link in top right */}
        <button
          className="welcome-modal-login-link"
          onClick={onLoginClick}
          type="button"
        >
          Login
        </button>

        <LogoModalHeader />

        {/* Headline */}
        <h1 className="welcome-modal-headline font-eagle-bold">Welcome to Vibe Stream!</h1>

        {/* Subheadline */}
        <p className="welcome-modal-subheadline">
          Start streaming with a free 14-day Party Pass.
        </p>

        {/* Google OAuth Button */}
        <GoogleOAuthButton />

        {/* Divider */}
        <div className="welcome-modal-divider">
          <span>OR</span>
        </div>

        {/* Email Signup Form */}
        <form className="welcome-modal-form" onSubmit={handleSubmit}>
          <input
            type="email"
            className="welcome-modal-input"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            autoComplete="email"
          />
          <button
            type="submit"
            className="welcome-modal-submit-button"
            disabled={isLoading}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </button>
          {error && <div className="welcome-modal-error">{error}</div>}
        </form>

        {/* Terms of Service */}
        <div className="welcome-modal-terms">
          By signing in, you agree to our{" "}
          <Link to="/terms" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </Link>
          .
        </div>
      </div>
    </ModalBox>
  );
}

export default WelcomeModal;
